import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../services/apiService";

export const LOAD_ERROR = "Couldn't load post-session quizzes. Nothing is shown rather than a partial list.";

// The one owner of the tutor's post-session quiz list. The dashboard's activity
// feed and the email tracker both read this, so the page makes one request.
// data: null = loading (from first paint), "dark" = backend feature off, else
// the response. Manual reload only; nothing polls.
export default function usePostSessionQuizzes(enabled = true) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  // Generation guard: unmount, or a newer load, bumps it so a reply that
  // arrives late is ignored (no state update after unmount, and a stale reply
  // can never overwrite the newer request's loading/result).
  const generation = useRef(0);

  const load = useCallback(async () => {
    generation.current += 1;
    const mine = generation.current;
    setError("");
    setData(null);
    let next = null;
    try {
      const res = await api.getTutorPostSessionQuizzes();
      if ((res?.errStatus || res?.status) === 404) {
        next = "dark";
      } else if (res && Array.isArray(res.rows) && !res.errMsg) {
        next = res;
      }
    } catch {
      next = null;
    }
    if (mine !== generation.current) return;
    if (next) setData(next);
    else setError(LOAD_ERROR);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    return () => {
      generation.current += 1;
    };
  }, [enabled, load]);

  return { data, error, load, generation };
}
